import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Clock, Calendar } from 'lucide-react';

function useWindowWidth() {
  const [w, setW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
}

export const BlogLayout = ({ children, title, description, date, readTime, category }) => {
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 640;

  return (
    <div style={{ minHeight: '100vh', background: '#020210', color: '#fff' }}>
      {/* Navbar */}
      <nav style={{
        background: 'rgba(2,2,16,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(112,0,255,0.3)',
        padding: isMobile ? '0 16px' : '0 24px',
        height: isMobile ? 52 : 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        gap: 8,
      }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}
          onClick={() => navigate('/')}
        >
          <Shield size={isMobile ? 18 : 22} color="#00F0FF" />
          <span style={{ fontWeight: 800, fontSize: isMobile ? 16 : 18, color: '#fff', fontFamily: 'var(--font-heading)' }}>
            Web<span style={{ color: '#00F0FF' }}>Shield</span>
          </span>
        </div>

        <div style={{ display: 'flex', gap: isMobile ? 6 : 12, flexShrink: 0 }}>
          <button
            onClick={() => navigate('/blog')}
            style={{
              background: 'transparent',
              border: '1px solid #2a2a4a',
              borderRadius: 8,
              padding: isMobile ? '5px 10px' : '6px 14px',
              color: '#b0b8d0',
              fontSize: isMobile ? 11 : 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            <ArrowLeft size={11} />
            {isMobile ? 'Back' : 'All Articles'}
          </button>
          <button
            onClick={() => navigate('/scanner')}
            style={{
              background: 'linear-gradient(135deg,#7000FF,#00F0FF)',
              border: 'none',
              borderRadius: 8,
              padding: isMobile ? '5px 10px' : '6px 16px',
              color: '#fff',
              fontWeight: 700,
              fontSize: isMobile ? 11 : 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? 'Scan →' : 'Scan My Site →'}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div style={{
        borderBottom: '1px solid rgba(42,42,74,0.5)',
        background: 'linear-gradient(180deg, rgba(112,0,255,0.06) 0%, transparent 100%)',
        padding: isMobile ? '32px 16px 28px' : '48px 24px 40px',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(112,0,255,0.15)',
            border: '1px solid rgba(112,0,255,0.4)',
            borderRadius: 20,
            padding: '4px 14px',
            fontSize: isMobile ? 10 : 12,
            color: '#00F0FF',
            fontWeight: 600,
            marginBottom: isMobile ? 14 : 20,
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}>
            {category}
          </div>

          <h1 style={{
            fontSize: isMobile ? 'clamp(20px,7vw,32px)' : 'clamp(24px,5vw,40px)',
            fontWeight: 800,
            lineHeight: 1.2,
            marginBottom: isMobile ? 12 : 16,
            fontFamily: 'var(--font-heading)',
            color: '#fff',
          }}>
            {title}
          </h1>

          <p style={{
            fontSize: isMobile ? 14 : 17,
            color: '#8898B0',
            lineHeight: 1.7,
            marginBottom: isMobile ? 16 : 24,
            maxWidth: 640,
          }}>
            {description}
          </p>

          <div style={{ display: 'flex', gap: isMobile ? 12 : 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6a7a9a', fontSize: isMobile ? 11 : 13 }}>
              <Calendar size={11} />{date}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6a7a9a', fontSize: isMobile ? 11 : 13 }}>
              <Clock size={11} />{readTime} min read
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '32px 16px 60px' : '48px 24px 80px' }}>
        <div style={{ fontSize: isMobile ? 15 : 16, lineHeight: 1.85, color: '#C0C8E0' }}>
          {children}
        </div>

        {/* CTA */}
        <div style={{
          marginTop: isMobile ? 48 : 64,
          padding: isMobile ? '24px 20px' : '32px',
          borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(112,0,255,0.15), rgba(0,240,255,0.08))',
          border: '1px solid rgba(112,0,255,0.3)',
          textAlign: 'center',
        }}>
          <Shield size={isMobile ? 30 : 36} color="#00F0FF" style={{ margin: '0 auto 12px' }} />
          <h3 style={{
            fontSize: isMobile ? 17 : 20,
            fontWeight: 700,
            color: '#fff',
            marginBottom: 8,
            fontFamily: 'var(--font-heading)',
          }}>
            Check Your Website Right Now
          </h3>
          <p style={{ color: '#8898B0', fontSize: isMobile ? 13 : 14, marginBottom: 20 }}>
            Get an instant security report with score, grade and actionable recommendations.
          </p>
          <button
            onClick={() => navigate('/scanner')}
            style={{
              background: 'linear-gradient(135deg,#7000FF,#00F0FF)',
              border: 'none',
              borderRadius: 10,
              padding: isMobile ? '12px 24px' : '13px 32px',
              color: '#fff',
              fontWeight: 700,
              fontSize: isMobile ? 14 : 15,
              cursor: 'pointer',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            Start Free Scan — ₹299 →
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reusable content components ──────────────────────────────────────────────

export const H2 = ({ children }) => (
  <h2 style={{
    fontSize: 'clamp(18px, 4vw, 24px)',
    fontWeight: 700,
    color: '#fff',
    margin: '36px 0 12px',
    fontFamily: 'var(--font-heading)',
    borderLeft: '3px solid #7000FF',
    paddingLeft: 14,
  }}>
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 style={{
    fontSize: 'clamp(15px, 3.5vw, 18px)',
    fontWeight: 700,
    color: '#00F0FF',
    margin: '24px 0 10px',
  }}>
    {children}
  </h3>
);

export const P = ({ children }) => (
  <p style={{ margin: '0 0 16px', lineHeight: 1.85 }}>{children}</p>
);

export const UL = ({ items }) => (
  <ul style={{ paddingLeft: 0, margin: '0 0 20px', listStyle: 'none' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
        <span style={{ color: '#00FF94', marginTop: 4, flexShrink: 0, fontSize: 12 }}>✓</span>
        <span style={{ lineHeight: 1.7 }}>{item}</span>
      </li>
    ))}
  </ul>
);

export const InfoBox = ({ title, children, color = '#00F0FF' }) => (
  <div style={{
    background: 'rgba(13,13,43,0.8)',
    border: `1px solid ${color}33`,
    borderLeft: `3px solid ${color}`,
    borderRadius: 10,
    padding: '16px 18px',
    margin: '20px 0',
  }}>
    {title && (
      <p style={{ fontWeight: 700, color, marginBottom: 8, fontSize: 13 }}>{title}</p>
    )}
    <div style={{ color: '#b0b8d0', fontSize: 13, lineHeight: 1.75 }}>{children}</div>
  </div>
);

export const ScoreCard = ({ label, score, color }) => (
  <div style={{
    background: '#0d0d2b',
    border: '1px solid #1a1a3e',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    gap: 12,
    flexWrap: 'wrap',
  }}>
    <span style={{ fontSize: 13, color: '#b0b8d0' }}>{label}</span>
    <span style={{ fontWeight: 700, color, fontSize: 13, flexShrink: 0 }}>{score}</span>
  </div>
);